/**
 * Auto-fixers for spec-validator issues.
 *
 * The spec-validator produces stable issue codes; this module turns the
 * subset of those codes with deterministic remediations into pure
 * (spec, issue) → spec functions. The renderer's edit-mode UI surfaces
 * a "Fix" button per issue that applies the fixer for the issue's code.
 *
 * Every fixer is non-destructive about the parts of the spec it isn't
 * remediating: it returns a structurally-shared deep clone where only
 * the affected branch is rebuilt.
 *
 * Issue codes NOT covered (intentionally):
 *  - `non_object_root` / `children_not_array`: the spec isn't usable
 *    as input — no path to walk to. The renderer falls back to the
 *    Zod-error screen for these.
 *  - `empty_spec`: warning, not an error — let the spec author add
 *    children themselves rather than auto-stubbing.
 *  - `repeat_missing_source` / `repeat_missing_item`: stripping the
 *    Repeat would silently drop content the AI generated; better to
 *    flag and let the author decide. The fixer stub is still wired so
 *    it can ship a guarded "remove this Repeat" follow-up later.
 */

import type { SpecIssue, SpecIssueCode } from "./spec-validator"
import { validateSpec } from "./spec-validator"

import type { DashboardSpec } from "../dashboard/catalog"

export interface ApplyFixOptions {
  /** Fail-fast: throw if the path can't be resolved. Default: silent no-op. */
  strict?: boolean
}

export interface FixResult {
  spec: DashboardSpec
  /** True iff the spec changed; lets the caller skip a no-op commit. */
  changed: boolean
}

/** Codes the click-to-fix UI knows how to remediate today. */
export const AUTO_FIXABLE_CODES: ReadonlySet<SpecIssueCode> = new Set<SpecIssueCode>([
  "visible_in_props",
  "watch_in_props",
  "repeat_in_props",
  "on_in_props",
  "unknown_check_type",
  "tabs_duplicate_value",
  "binding_target_missing",
  "unknown_type",
])

/**
 * Apply the fixer for ``issue`` to ``spec`` and return the updated spec.
 * No-op when ``issue.code`` isn't in {@link AUTO_FIXABLE_CODES} or the
 * referenced path can't be resolved.
 */
export function applySpecFix(
  spec: DashboardSpec,
  issue: SpecIssue,
  options: ApplyFixOptions = {}
): FixResult {
  if (!AUTO_FIXABLE_CODES.has(issue.code)) {
    return { spec, changed: false }
  }
  if (!issue.elementPath && issue.code !== "binding_target_missing") {
    return { spec, changed: false }
  }

  const next = structuredClone(spec) as DashboardSpec
  let changed = false

  switch (issue.code) {
    case "visible_in_props":
    case "watch_in_props":
    case "repeat_in_props": {
      const key =
        issue.code === "visible_in_props"
          ? "visible"
          : issue.code === "watch_in_props"
            ? "watch"
            : "repeat"
      changed = hoistFromProps(next, issue.elementPath!, key, options)
      break
    }
    case "on_in_props": {
      // The misplaced key name is in the message — extract the prop
      // name from `\`onClick\` should live at the element's top level...`
      const match = issue.message.match(/`(on[A-Z][^`]*)`/)
      if (!match) break
      changed = hoistFromProps(next, issue.elementPath!, match[1]!, options)
      break
    }
    case "unknown_check_type": {
      changed = stripIndexedNode(next, issue.elementPath!, "checks", options)
      break
    }
    case "tabs_duplicate_value": {
      changed = renameDuplicateTabValue(next, issue.elementPath!, options)
      break
    }
    case "binding_target_missing": {
      changed = stripBindingByPath(next, issue.elementPath ?? null)
      break
    }
    case "unknown_type": {
      changed = stripElement(next, issue.elementPath!, options)
      break
    }
    default:
      break
  }

  return { spec: changed ? next : spec, changed }
}

/**
 * Apply every deterministic repair, re-validating after each mutation.
 *
 * Re-validation is important for array removals: fixing `children[4]`
 * shifts the old `children[5]` to index 4. Replaying the original issue
 * list would therefore skip adjacent invalid elements and leave the user
 * needing a reload plus a second repair pass.
 */
export function applyAllAutoFixableSpecIssues(spec: DashboardSpec): FixResult {
  let working = spec
  let anyChanged = false

  // The cap is defensive against a future fixer that reports a mutation
  // without actually clearing its validation issue.
  for (let pass = 0; pass < 100; pass += 1) {
    const issues = validateSpec(working).issues.filter((issue) =>
      AUTO_FIXABLE_CODES.has(issue.code)
    )
    if (issues.length === 0) break

    let passChanged = false
    for (const issue of issues) {
      const result = applySpecFix(working, issue)
      if (!result.changed) continue
      working = result.spec
      anyChanged = true
      passChanged = true
      break
    }
    if (!passChanged) break
  }

  return { spec: working, changed: anyChanged }
}

// ── helpers ─────────────────────────────────────────────────────────

/**
 * Move ``element.props[key]`` up to ``element[key]`` if and only if the
 * top-level slot is empty. Lossy carry-up (top-level wins) would silently
 * overwrite an explicit author choice; we'd rather leave the misplaced
 * copy in place and surface the conflict on the next validation pass.
 */

function hoistFromProps(
  spec: DashboardSpec,
  path: string,
  key: string,
  options: ApplyFixOptions
): boolean {
  const node = resolveElementByPath(spec, path)
  if (!node) {
    if (options.strict) throw new Error(`spec-fixers: unresolved path ${path}`)
    return false
  }
  const props = (node as Record<string, unknown>).props
  if (!props || typeof props !== "object" || Array.isArray(props)) return false
  const propsRec = props as Record<string, unknown>
  if (!(key in propsRec)) return false
  const value = propsRec[key]
  delete propsRec[key]
  if (key in (node as Record<string, unknown>)) {
    // Conflict — keep top-level, drop the misplaced copy.
    return true
  }
  ;(node as Record<string, unknown>)[key] = value
  return true
}

/**
 * Strip the indexed sub-element targeted by an ``...checks[i]``-style
 * path. Falls through to no-op if the index doesn't exist.
 */

function stripIndexedNode(
  spec: DashboardSpec,
  path: string,
  collection: string,
  options: ApplyFixOptions
): boolean {
  // Path shape: children[3].checks[1] — pop the last [n], navigate to
  // parent + collection, splice out the index.
  const match = path.match(/^(.*)\.([a-zA-Z_][\w]*)\[(\d+)\]$/)
  if (!match) {
    if (options.strict) throw new Error(`spec-fixers: bad indexed path ${path}`)
    return false
  }
  const [, parentPath, fieldName, indexStr] = match
  if (fieldName !== collection) return false
  const parent = resolveElementByPath(spec, parentPath!)
  if (!parent) return false
  const arr = (parent as Record<string, unknown>)[collection]
  if (!Array.isArray(arr)) return false
  const idx = Number(indexStr)
  if (idx < 0 || idx >= arr.length) return false
  arr.splice(idx, 1)
  return true
}

/**
 * Append a numeric suffix to a duplicate tab.value so it becomes unique
 * within its Tabs parent. Picks the smallest suffix not already in use.
 */
function renameDuplicateTabValue(
  spec: DashboardSpec,
  path: string,
  options: ApplyFixOptions
): boolean {
  // Path shape: children[2].tabs[3]
  const match = path.match(/^(.*)\.tabs\[(\d+)\]$/)
  if (!match) {
    if (options.strict) throw new Error(`spec-fixers: bad tabs path ${path}`)
    return false
  }
  const [, parentPath, indexStr] = match
  const parent = resolveElementByPath(spec, parentPath!)
  if (!parent) return false
  const tabs = (parent as Record<string, unknown>).tabs
  if (!Array.isArray(tabs)) return false
  const idx = Number(indexStr)
  const target = tabs[idx]
  if (!target || typeof target !== "object") return false
  const tabRec = target as Record<string, unknown>
  const original = typeof tabRec.value === "string" ? tabRec.value : null
  if (!original) return false

  const existing = new Set<string>()
  for (let i = 0; i < tabs.length; i++) {
    if (i === idx) continue
    const t = tabs[i]
    if (t && typeof t === "object" && typeof (t as Record<string, unknown>).value === "string") {
      existing.add((t as Record<string, unknown>).value as string)
    }
  }
  let suffix = 2
  let next = `${original}-${suffix}`
  while (existing.has(next)) {
    suffix += 1
    next = `${original}-${suffix}`
  }
  tabRec.value = next
  return true
}

/**
 * Drop a queryBinding whose ``element_path`` matches ``path``. Identifies
 * the matching binding by the same path the validator walked, falling
 * back to a tile-level prefix match (``children[2].data`` → ``children[2]``).
 */
function stripBindingByPath(spec: DashboardSpec, path: string | null): boolean {
  if (!path) return false
  const bindings = (spec as Record<string, unknown>).queryBindings
  if (!Array.isArray(bindings)) return false
  const tilePath = path.replace(/\.[^.]+$/, "")
  const before = bindings.length
  ;(spec as Record<string, unknown>).queryBindings = bindings.filter((b) => {
    if (!b || typeof b !== "object") return true
    const bp = (b as Record<string, unknown>).element_path
    return bp !== path && bp !== tilePath
  })
  return ((spec as Record<string, unknown>).queryBindings as unknown[]).length !== before
}

/**
 * Remove an element by its dotted path. Splices out of the parent's
 * children/tabs/items collection. Refuses to strip the root.
 */
function stripElement(spec: DashboardSpec, path: string, options: ApplyFixOptions): boolean {
  // Match the trailing collection-and-index, e.g. children[3] or
  // tabs[2].children[0]
  const match = path.match(/^(.*?)\.?([a-zA-Z_][\w]*)\[(\d+)\]$/)
  if (!match) {
    if (options.strict) throw new Error(`spec-fixers: bad strip path ${path}`)
    return false
  }
  const [, parentPath, collection, indexStr] = match
  const parent = parentPath ? resolveElementByPath(spec, parentPath) : (spec as unknown)
  if (!parent || typeof parent !== "object") return false
  const arr = (parent as Record<string, unknown>)[collection!]
  if (!Array.isArray(arr)) return false
  const idx = Number(indexStr)
  if (idx < 0 || idx >= arr.length) return false
  rebaseBindingsAfterRemoval(spec, path)
  arr.splice(idx, 1)
  return true
}

/** Keep binding paths aligned when a deterministic repair removes a tile. */
function rebaseBindingsAfterRemoval(spec: DashboardSpec, removedPath: string): void {
  const bindings = (spec as Record<string, unknown>).queryBindings
  if (!Array.isArray(bindings)) return

  const pathMatch = removedPath.match(/^(.*?)\.?([a-zA-Z_][\w]*)\[(\d+)\]$/)
  if (!pathMatch) return
  const [, parentPath, collection, removedIndexText] = pathMatch
  const collectionPath = `${parentPath ? `${parentPath}.` : ""}${collection}`
  const escapedCollectionPath = collectionPath.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const siblingPattern = new RegExp(`^${escapedCollectionPath}\\[(\\d+)\\](.*)$`)
  const removedIndex = Number(removedIndexText)

  ;(spec as Record<string, unknown>).queryBindings = bindings.flatMap((binding) => {
    if (!binding || typeof binding !== "object" || Array.isArray(binding)) return [binding]
    const bindingRecord = binding as Record<string, unknown>
    const elementPath = bindingRecord.element_path
    if (typeof elementPath !== "string") return [binding]

    const bindingMatch = elementPath.match(siblingPattern)
    if (!bindingMatch) return [binding]
    const bindingIndex = Number(bindingMatch[1])
    if (bindingIndex === removedIndex) return []
    if (bindingIndex < removedIndex) return [binding]

    return [
      {
        ...bindingRecord,
        element_path: `${collectionPath}[${bindingIndex - 1}]${bindingMatch[2]}`,
      },
    ]
  })
}

/**
 * Resolve a dotted path like `children[2].tabs[0].children[1]` into the
 * concrete object inside the spec tree. Returns ``null`` for unresolvable
 * paths so callers can no-op cleanly.
 */
export function resolveElementByPath(spec: DashboardSpec, path: string): unknown {
  if (!path) return spec
  const segments = path.match(/[a-zA-Z_][\w]*|\[\d+\]/g) ?? []
  let current: unknown = spec
  for (const seg of segments) {
    if (current === null || typeof current !== "object") return null
    if (seg.startsWith("[")) {
      const idx = Number(seg.slice(1, -1))
      if (!Array.isArray(current) || idx < 0 || idx >= current.length) return null
      current = current[idx]
    } else {
      current = (current as Record<string, unknown>)[seg]
    }
  }
  return current
}
